import { Dropdown } from "monday-ui-react-core";
import React, { useEffect, useId, useMemo } from "react";
import styled from "styled-components";

import type { DropdownOption, DropdownOptionBase } from "./models/dropdownOptions";
import type { MenuRendererProps } from "./models/menuRenderProps";

type DropdownStyles = {
  marginSize?: string;
  error?: string;
};

export type DropdownProps = {
  id?: string;
  value?: DropdownOption | null;
  defaultValue?: DropdownOption;
  options: DropdownOption[];
  cacheOptions?: boolean;
  placeholder?: string;
  menuPlacement?: "bottom" | "top" | "auto";
  tabIndex?: number;
  disabled?: boolean;
  clearable?: boolean;
  insideOverflowContainer?: boolean;
  className?: string;
  onOptionSelect?: (props: DropdownOptionBase) => void;
  optionRenderer?: (props: DropdownOptionBase) => JSX.Element;
  valueRenderer?: (props: DropdownOptionBase) => JSX.Element;
  onClear?: () => void;
  menuRenderer?: (props: MenuRendererProps<object>) => JSX.Element;
  onInputChange?: (data: string) => void;
  openMenuOnFocus?: boolean;
  openMenuOnClick?: boolean;
  tabSelectsValue?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  onKeyDownCapture?: (event: KeyboardEvent) => void;
  onChange?: (option?: DropdownOption) => void;
  loadingMessage?: string;
} & DropdownStyles;

const DropDownWrapper = styled(Dropdown)<DropdownStyles>`
  width: 100%;
  margin: ${(props) => props.marginSize};
  ${(props) => (props.error ? `border: 1px solid var(--negative-color) !important;` : "")};
`;

export const DropdownList: React.FC<DropdownProps> = ({ error, className, ...props }) => {
  const id = useId();
  const dropdownId = useMemo<string>(() => `dropdown-${id}`, [id]);
  const divElement = useMemo(() => document.createElement("div"), []);

  useEffect(() => {
    const dropdown = document.getElementById(dropdownId);
    const dropdownInput = dropdown?.firstChild;

    divElement.id = `spinner-container-${dropdownId}`;
    if (dropdownInput && dropdownInput.firstChild) {
      dropdownInput.firstChild.insertBefore(divElement, dropdownInput.firstChild.lastChild);
    }
  }, [divElement, dropdownId]);

  return (
    <React.Fragment>
      <DropDownWrapper
        {...props}
        id={dropdownId}
        error={error}
        className={className}
        openMenuOnFocus={false}
        openMenuOnClick={true}
        tabSelectsValue={false}
        tabIndex={props.tabIndex ?? 0}
      />
    </React.Fragment>
  );
};
